import React, { useEffect, useState } from "react";
import { Plus, Send, X, UserPlus, QrCode } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Badge, Empty, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [show, setShow] = useState(false);
  const [quick, setQuick] = useState(null);
  const load = () => api.get("/campaigns").then(({ data }) => setCampaigns(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Campaigns & Outreach" subtitle="Create outreach campaigns and add contacts with fast quick-entry.">
        <RedButton onClick={() => setShow(true)} data-testid="campaign-create"><Plus size={15} /> Create Campaign</RedButton>
      </PageHeader>
      {campaigns.length === 0 ? (
        <Card className="p-6"><Empty icon={Send} title="No campaigns yet" hint="Create an outreach campaign, then add contacts in seconds." action={<RedButton onClick={() => setShow(true)}><Plus size={15} /> Create Campaign</RedButton>} /></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between"><h3 className="font-display font-bold text-slate-900">{c.name}</h3><Badge>{c.source}</Badge></div>
              <p className="text-xs text-slate-500 mt-1">{c.type}</p>
              <RedButton className="w-full mt-4 !py-2" onClick={() => setQuick(c)} data-testid={`campaign-add-${c.id}`}><UserPlus size={14} /> Quick Add Contact</RedButton>
            </Card>
          ))}
        </div>
      )}
      {show && <CampaignModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {quick && <QuickModal campaign={quick} onClose={() => setQuick(null)} />}
    </div>
  );
}

function CampaignModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", source: "Outreach", type: "Outreach" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { if (!form.name) { toast.error("Name required"); return; } try { await api.post("/campaigns", form); toast.success("Campaign created"); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h2 className="font-display text-xl font-bold">Create Campaign</h2><button onClick={onClose} className="text-slate-400"><X size={20} /></button></div>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="campaign-name" placeholder="e.g. St. Marys Downtown Outreach" /></Field>
          <Field label="Source"><select className={inputCls} value={form.source} onChange={set("source")} data-testid="campaign-source">{["Outreach","Event","Church Service","Advertising","Referral","Other"].map((s) => <option key={s}>{s}</option>)}</select></Field>
        </div>
        <div className="flex gap-3 mt-6"><GhostButton className="flex-1" onClick={onClose}>Cancel</GhostButton><RedButton className="flex-1" onClick={save} data-testid="campaign-save">Create</RedButton></div>
      </Card>
    </div>
  );
}

function QuickModal({ campaign, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try { const { data } = await api.post("/contacts/quick", { ...form, source: campaign.source, campaign: campaign.name }); toast.success(data.created ? "Contact added + follow-up created" : "Existing contact updated"); setForm({ name: "", phone: "", email: "", notes: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><h2 className="font-display text-xl font-bold">Quick Add</h2><button onClick={onClose} className="text-slate-400"><X size={20} /></button></div>
        <p className="text-sm text-slate-500 mb-5">{campaign.name} · auto source = {campaign.source}</p>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="quick-name" autoFocus /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="quick-phone" /></Field>
            <Field label="Email"><input className={inputCls} value={form.email} onChange={set("email")} data-testid="quick-email" /></Field>
          </div>
          <Field label="Notes"><input className={inputCls} value={form.notes} onChange={set("notes")} data-testid="quick-notes" /></Field>
        </div>
        <div className="flex gap-3 mt-6"><GhostButton className="flex-1" onClick={onClose}>Done</GhostButton><RedButton className="flex-1" onClick={save} data-testid="quick-save">Add & Next</RedButton></div>
      </Card>
    </div>
  );
}
